import { ArrowLeft, X } from "@phosphor-icons/react";
import { type KeyboardEvent, type PointerEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ConversationMessage, MessageDraft } from "~/api/conversationTypes";
import {
  useConversationDetail,
  useRethinkMessage,
  useReviseMessage,
  useRunConversationPrompt,
  useSendConversationMessage,
  useSourceTraces,
} from "~/api/queries";
import { ABILITY, type Document } from "~/api/types";
import { CommentsPanel } from "~/features/comments/CommentsPanel";
import { ConversationCreateDialog } from "~/features/conversations/ConversationCreateDialog";
import { ConversationList } from "~/features/conversations/ConversationList";
import { DocumentChatPanel } from "~/features/conversations/chat/DocumentChatPanel";
import { ContextPanel } from "~/features/conversations/context/ContextPanel";
import {
  CONVERSATION_DENSITIES,
  type ConversationDensity,
  type ConversationTab,
  DENSITY_INITIAL,
  readDensity,
  resolveTab,
  TAB_GLYPH,
  tabsFor,
  writeDensity,
} from "~/features/conversations/drawerTabs";
import { OutcomesPanel, OutputsList } from "~/features/conversations/outcomes/OutcomesPanel";
import { DocumentAiSettings } from "~/features/conversations/participants/DocumentAiSettings";
import { ThinkerPanel } from "~/features/conversations/participants/ThinkerPanel";
import type { DocumentPorts } from "~/features/conversations/ports/editorPorts";
import { MessageSources } from "~/features/conversations/skills/MessageSources";
import { parseSkills } from "~/features/conversations/skills/parseSkills";
import { SkillCard } from "~/features/conversations/skills/SkillCard";
import { SourcesPanel } from "~/features/conversations/skills/SourcesPanel";
import {
  Composer,
  type ComposerValue,
  EMPTY_COMPOSER,
} from "~/features/conversations/transcript/Composer";
import type { MessageActions } from "~/features/conversations/transcript/MessageItem";
import { Transcript } from "~/features/conversations/transcript/Transcript";
import { useConversationStream } from "~/features/conversations/transcript/useConversationStream";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Kbd } from "~/foundation/components/Kbd";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { Tooltip } from "~/foundation/components/Tooltip";
import { depths } from "~/foundation/styles/depths";
import { cn } from "~/foundation/utils/cn";
import { usePolicy } from "~/policies/usePolicy";
import { RIGHT_DRAWER_MAX_WIDTH, RIGHT_DRAWER_MIN_WIDTH, useShellStore } from "~/shell/store";

/**
 * The tab strip's words. The glyph is decoration and lives in `drawerTabs`; the LABEL is the
 * accessible name, so it is translated at the call site and kept here rather than in the
 * registry, which knows nothing about language.
 */
const TAB_LABEL: Record<ConversationTab, (t: (key: string) => string) => string> = {
  chat: (t) => t("Chat"),
  timeline: (t) => t("Timeline"),
  context: (t) => t("Context"),
  thinkers: (t) => t("Thinkers"),
  sources: (t) => t("Sources"),
  outputs: (t) => t("Outputs"),
};

/**
 * The document right drawer (W8 §B.1).
 *
 * It mounts inside the document scene rather than the shell layout, which is what makes kms's
 * `EditorRegistry` unnecessary: that store existed ONLY because the panels were mounted above
 * the document context and had no other way to reach the editor. Here the host already has the
 * document, the policy and (once W6 lands them) the editor ports.
 *
 * `Escape` is scoped to the drawer — kms registered a module-global key handler that fired
 * from anywhere on the page.
 */
export function DocumentDrawer({
  document,
  collectionId,
  ports,
}: {
  document: Document;
  collectionId: string | null;
  /**
   * The editor ports (§D.0). Supplied by the scene; ABSENT is a supported configuration and
   * every panel that needs one renders its affordance disabled with the reason.
   */
  ports: DocumentPorts;
}) {
  const { t } = useTranslation();
  const rightDrawer = useShellStore((state) => state.rightDrawer);
  const width = useShellStore((state) => state.rightDrawerWidth);
  const resizing = rightDrawer.resizing;
  const close = useShellStore((state) => state.closeRightDrawer);
  const open = useShellStore((state) => state.openRightDrawer);
  const setWidth = useShellStore((state) => state.setRightDrawerWidth);
  const resetWidth = useShellStore((state) => state.resetRightDrawerWidth);
  const setResizing = useShellStore((state) => state.setRightDrawerResizing);

  const can = usePolicy(document);
  const canUpdate = can[ABILITY.documentUpdate] === true;
  // A conversation is a comment on the document, and that is the only gate the server does not
  // have: not one conversation endpoint projects a policy (W8 §0.3).
  const canComment = can[ABILITY.documentComment] === true;

  const [density, setDensity] = useState<ConversationDensity>(() => readDensity());
  const [tab, setTab] = useState<ConversationTab>("timeline");
  const [createOpen, setCreateOpen] = useState(false);
  const [composer, setComposer] = useState<ComposerValue>(EMPTY_COMPOSER);

  const conversationId = rightDrawer.kind === "conversation" ? rightDrawer.entityId : null;
  const detail = useConversationDetail(conversationId, { poll: true });
  const conversation = detail.data ?? null;
  const stream = useConversationStream(conversationId);
  // S7 — document-wide provenance. One read serves both the Sources tab and the per-message
  // disclosure; they are the same rows, joined by `metadata.streamId`.
  const traces = useSourceTraces(document.id);
  const revise = useReviseMessage();
  const rethink = useRethinkMessage();
  const send = useSendConversationMessage();
  const runPrompt = useRunConversationPrompt();

  // A new conversation starts on Timeline and with an empty composer, every time.
  useEffect(() => {
    setTab("timeline");
    setComposer(EMPTY_COMPOSER);
  }, []);

  /**
   * Only the tabs whose panel this build actually has. S6–S8 add `context`, `sources` and
   * `outputs` by registering them here; until then the strip never offers a dead end.
   */
  const available = useMemo(
    () =>
      new Set<ConversationTab>(["chat", "timeline", "context", "thinkers", "sources", "outputs"]),
    [],
  );
  const tabs = tabsFor(density, available);
  const activeTab = resolveTab(tab, density, available);

  const actions = useMemo<MessageActions>(
    () => ({
      canEdit: canUpdate,
      canReview: canComment,
      onRevise: async (messageId, content) => {
        await revise.mutateAsync({ messageId, patch: { content } });
      },
      onRewriteFrom: async (messageId, content) => {
        if (conversationId) {
          await rethink.mutateAsync({ id: conversationId, messageId, updatedContent: content });
        }
      },
      onReview: (message: ConversationMessage, rating: number, selection: string) => {
        if (!conversationId) {
          return;
        }
        // kms's ✓/✗ were `// TODO: Call API` toasts (W8-16). This is the write they needed:
        // a `moderator_review` row the aggregation can actually read.
        const draft: MessageDraft = {
          content: `Moderator review rated: ${selection}`,
          authorType: "human",
          messageType: "moderator_review",
          metadata: {
            targetMessageId: message.id,
            selection,
            rating,
            action: rating === 0 ? "cleared" : "rated",
            reviewedAt: new Date().toISOString(),
          },
        };
        send.mutate({ id: conversationId, message: draft });
      },
      onHide: () => undefined,
    }),
    [canComment, canUpdate, conversationId, rethink, revise, send],
  );

  /**
   * One slot, three panels (§B.1). kms mounted eight mutually-exclusive right panels held
   * apart by eight booleans; here the exclusion is structural — the drawer renders whichever
   * kind is in the slot, and there is nowhere for a second one to be.
   */
  const kind = rightDrawer.kind;
  if (kind !== "conversation" && kind !== "chat" && kind !== "comments") {
    return null;
  }
  const title =
    kind === "chat"
      ? t("Chat")
      : kind === "comments"
        ? t("Comments")
        : conversationId
          ? (conversation?.title ?? t("Conversation"))
          : t("Conversations");

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    setResizing(true);
    const rtl =
      typeof window !== "undefined" &&
      window.getComputedStyle(window.document.documentElement).direction === "rtl";
    const onMove = (move: globalThis.PointerEvent) => {
      // The drawer sits on the inline END, so in RTL its edge is measured from the left.
      setWidth(rtl ? move.clientX : window.innerWidth - move.clientX);
    };
    const onUp = () => {
      setResizing(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape") {
      return;
    }
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName.toLowerCase();
    if (tag === "input" || tag === "textarea" || target?.isContentEditable) {
      return;
    }
    close();
  };

  const committed = conversation?.isCommitted === true;
  const disabledReason = !canComment
    ? t("You can read this conversation, but not add to it.")
    : committed
      ? t("Reopen the conversation to add messages")
      : null;

  const participants = (conversation?.thinkerIds ?? []).map((id) => ({ id, name: id }));

  const submit = () => {
    if (!conversationId || !composer.content.trim()) {
      return;
    }
    const content = composer.content.trim();
    setComposer({ ...composer, content: "" });
    const optionsChosen =
      composer.promptType !== "ask" ||
      composer.outputFormat !== "freeform" ||
      composer.thoughtLevel !== "medium" ||
      composer.facet !== "" ||
      composer.targetThinkerIds.length > 0;

    if (optionsChosen) {
      // The one endpoint that takes the whole taxonomy. kms collected it and threw it away.
      runPrompt.mutate({
        id: conversationId,
        run: {
          content,
          promptType: composer.promptType,
          outputFormat: composer.outputFormat,
          thoughtLevel: composer.thoughtLevel,
          ...(composer.facet ? { facetTags: [composer.facet] } : {}),
          ...(composer.targetThinkerIds.length > 0
            ? { targetThinkerIds: composer.targetThinkerIds }
            : {}),
        },
      });
      return;
    }
    void stream.send({ content, authorType: "human", messageType: "text" });
  };

  return (
    <>
      {/* Mobile: a backdrop, because the drawer covers the document there. */}
      <button
        type="button"
        aria-label={t("Close the panel")}
        onClick={close}
        className="fixed inset-0 bg-backdrop md:hidden"
        style={{ zIndex: depths.sidebar - 1 }}
      />
      <aside
        aria-label={title}
        onKeyDown={onKeyDown}
        className={cn(
          "fixed inset-y-0 end-0 flex w-full flex-col border-s border-divider bg-bg",
          "md:w-[var(--drawer-width)]",
          resizing ? null : "transition-[width] duration-100 motion-reduce:transition-none",
        )}
        style={
          {
            zIndex: depths.sidebar,
            "--drawer-width": `${width}px`,
          } as Record<string, string | number>
        }
      >
        {/* Drag the inline-start border; double-click resets. Width never syncs across windows. */}
        <div
          role="separator"
          aria-label={t("Resize drawer")}
          aria-orientation="vertical"
          aria-valuenow={width}
          aria-valuemin={RIGHT_DRAWER_MIN_WIDTH}
          aria-valuemax={RIGHT_DRAWER_MAX_WIDTH}
          tabIndex={-1}
          onPointerDown={onPointerDown}
          onDoubleClick={resetWidth}
          className="absolute inset-y-0 start-0 hidden w-1 cursor-col-resize hover:bg-accent md:block"
        />

        <header className="flex items-center gap-2 border-b border-divider p-2">
          {kind === "conversation" && conversationId ? (
            <IconButton
              icon={ArrowLeft}
              size="sm"
              aria-label={t("Back to conversations")}
              className="rtl:rotate-180"
              onClick={() => open("conversation", null)}
            />
          ) : null}
          <Text variant="small" truncate className="min-w-0 flex-1 font-medium text-text">
            {title}
          </Text>

          {kind === "conversation" && conversationId ? (
            <div className="flex items-center gap-0.5" role="group" aria-label={t("Detail level")}>
              {CONVERSATION_DENSITIES.map((entry) => (
                <button
                  key={entry}
                  type="button"
                  aria-pressed={density === entry}
                  onClick={() => {
                    setDensity(entry);
                    writeDensity(entry);
                  }}
                  className={cn(
                    "h-6 w-6 rounded-sm text-caption",
                    density === entry
                      ? "bg-accent text-accent-text"
                      : "text-text-tertiary hover:bg-hover-bg",
                  )}
                >
                  {DENSITY_INITIAL[entry]}
                </button>
              ))}
            </div>
          ) : null}

          <Tooltip content={<Kbd keys={["Esc"]} />}>
            <IconButton icon={X} size="sm" aria-label={t("Close")} onClick={close} />
          </Tooltip>
        </header>

        {kind === "conversation" && conversationId ? (
          <nav className="flex gap-1 border-b border-divider px-2 py-1" aria-label={t("Panels")}>
            {tabs.map((entry) => (
              <button
                key={entry}
                type="button"
                aria-current={entry === activeTab}
                onClick={() => setTab(entry)}
                className={cn(
                  "rounded-sm px-2 py-1 text-caption",
                  entry === activeTab
                    ? "bg-accent-subtle text-accent"
                    : "text-text-tertiary hover:bg-hover-bg",
                )}
              >
                <span aria-hidden>{TAB_GLYPH[entry]} </span>
                {TAB_LABEL[entry](t)}
              </button>
            ))}
          </nav>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
          {kind === "chat" ? (
            <DocumentChatPanel documentId={document.id} canComment={canComment} />
          ) : kind === "comments" ? (
            <CommentsPanel document={document} ports={ports} />
          ) : !conversationId ? (
            <ConversationList
              documentId={document.id}
              canComment={canComment}
              canUpdate={canUpdate}
              activeId={null}
              onOpen={(id) => open("conversation", id)}
              onCreate={() => setCreateOpen(true)}
            />
          ) : detail.isLoading ? (
            <Spinner label={t("Loading")} />
          ) : !conversation ? (
            <Empty message={t("This conversation could not be loaded.")} />
          ) : activeTab === "thinkers" ? (
            <ThinkerPanel
              conversationId={conversation.id}
              documentId={document.id}
              canUpdate={canUpdate}
            />
          ) : activeTab === "outputs" ? (
            <div className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
              <OutcomesPanel
                conversation={conversation}
                document={document}
                ports={ports}
                canUpdate={canUpdate}
              />
              <div className="border-t border-divider pt-2">
                <Text variant="small" className="mb-1 font-medium text-text">
                  {t("Outputs")}
                </Text>
                <OutputsList conversation={conversation} />
              </div>
            </div>
          ) : activeTab === "sources" ? (
            <SourcesPanel documentId={document.id} />
          ) : activeTab === "context" ? (
            <ContextPanel
              conversationId={conversation.id}
              documentId={document.id}
              documentTitle={document.title}
              canComment={canComment}
              ports={ports}
            />
          ) : (
            <>
              <Transcript
                conversation={conversation}
                stream={stream.state}
                actions={actions}
                mode={activeTab === "chat" ? "chat" : "timeline"}
                messageExtras={(message) => {
                  // Skills are parsed out of the FINALISED body; while a turn streams they
                  // already show as live chips on the row (which kms could not do at all —
                  // it dropped both skill events).
                  const skills = parseSkills(message.id, message.content);
                  return (
                    <>
                      {skills.map((invocation) => (
                        <SkillCard
                          key={invocation.id}
                          invocation={invocation}
                          document={document}
                          ports={ports}
                          agent={message.author || "AI"}
                        />
                      ))}
                      <MessageSources message={message} traces={traces.data ?? []} />
                    </>
                  );
                }}
              />
              <Composer
                value={composer}
                onChange={setComposer}
                onSend={submit}
                onStop={stream.stop}
                busy={stream.state.running}
                disabledReason={disabledReason}
                participants={participants}
              />
            </>
          )}

          {kind === "conversation" && !conversationId ? (
            <div className="border-t border-divider pt-2">
              <DocumentAiSettings
                documentId={document.id}
                collectionId={collectionId}
                canUpdate={canUpdate}
              />
            </div>
          ) : null}
        </div>
      </aside>

      <ConversationCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        documentId={document.id}
        onCreated={(id) => open("conversation", id)}
      />
    </>
  );
}
